/* GGL media controls. Native Shopify gallery, cart and wishlist remain authoritative. */
(() => {
  'use strict';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function bindDrag(track, signal, settle) {
    let gesture, suppressUntil = 0;
    track.addEventListener('dragstart', event => event.preventDefault(), { signal });
    track.addEventListener('pointerdown', event => {
      if (event.button !== 0) return;
      gesture = { x: event.clientX, y: event.clientY, left: track.scrollLeft, index: Math.round(track.scrollLeft / track.clientWidth), mouse: event.pointerType === 'mouse', moved: false };
    }, { signal, passive: true });
    track.addEventListener('pointermove', event => {
      if (!gesture) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) gesture.moved = true;
      if (gesture.moved) suppressUntil = performance.now() + 500;
      if (!gesture.mouse || !gesture.moved) return;
      if (!track.hasPointerCapture(event.pointerId)) track.setPointerCapture(event.pointerId);
      track.classList.add('is-dragging');
      track.scrollLeft = gesture.left - dx;
      event.preventDefault();
    }, { signal });
    const end = event => {
      if (!gesture) return;
      const previous = gesture;
      gesture = null;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      if (previous.mouse && previous.moved) {
        const delta = event.clientX - previous.x;
        const target = Math.abs(delta) > Math.min(45, track.clientWidth * .2) ? previous.index + (delta < 0 ? 1 : -1) : previous.index;
        settle(target);
      }
    };
    track.addEventListener('pointerup', end, { signal });
    track.addEventListener('pointercancel', end, { signal });
    track.addEventListener('click', event => {
      if (performance.now() < suppressUntil) { event.preventDefault(); event.stopPropagation(); }
    }, { signal, capture: true });
  }
  if (!customElements.get('ggl-card-carousel')) {
    customElements.define('ggl-card-carousel', class extends HTMLElement {
      connectedCallback() {
        if (this.abort) return;
        this.track = this.querySelector('.ggl-card-carousel__track');
        this.slides = [...this.querySelectorAll('.ggl-card-carousel__slide')];
        if (!this.track || this.slides.length < 2) return;
        this.abort = new AbortController();
        const signal = this.abort.signal;
        this.dots = [...this.querySelectorAll('[data-index]')];
        this.previous = this.querySelector('[data-direction="-1"]');
        this.next = this.querySelector('[data-direction="1"]');
        this.index = 0;
        this.classList.add('is-ready');
        this.querySelectorAll('[hidden]').forEach(element => { element.hidden = false; });
        this.addEventListener('click', event => {
          const control = event.target.closest('button');
          if (!control || !this.contains(control)) return;
          event.preventDefault();
          const index = control.hasAttribute('data-index') ? Number(control.dataset.index) : this.index + Number(control.dataset.direction);
          this.go(index);
        }, { signal });
        this.addEventListener('keydown', event => {
          const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          event.preventDefault();
          this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }, { signal });
        this.track.addEventListener('scroll', () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
        }, { signal, passive: true });
        bindDrag(this.track, signal, index => this.go(index));
        this.resize = new ResizeObserver(() => {
          this.track.scrollTo({left: this.index * this.track.clientWidth, behavior: 'instant'});
        });
        this.resize.observe(this.track);
        this.update();
      }
      load(index) {
        const template = this.slides[index]?.querySelector('template');
        if (template) template.replaceWith(template.content.cloneNode(true));
      }
      go(index) {
        const target = Math.max(0, Math.min(index, this.slides.length - 1));
        this.load(target);
        this.track.scrollTo({left: target * this.track.clientWidth, behavior: reducedMotion() ? 'instant' : 'smooth'});
      }
      update() {
        if (!this.track.clientWidth) return;
        const index = Math.max(0, Math.min(Math.round(this.track.scrollLeft / this.track.clientWidth), this.slides.length - 1));
        const changed = index !== this.index;
        this.index = index;
        this.load(index); this.load(index + 1);
        this.dots.forEach((dot, position) => { if (position === index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
        this.slides.forEach((slide, position) => { slide.tabIndex = position === index ? 0 : -1; slide.setAttribute('aria-hidden', String(position !== index)); });
        this.previous.disabled = index === 0;
        this.next.disabled = index === this.slides.length - 1;
        if (changed) this.querySelector('[data-carousel-status]').textContent = 'სურათი ' + (index + 1) + ' / ' + this.slides.length;
      }
      disconnectedCallback() {
        this.abort?.abort(); this.abort = null; this.resize?.disconnect(); cancelAnimationFrame(this.frame); this.frame = 0;
      }
    });
  }
  function enhanceGallery(root = document) {
    root.querySelectorAll('.ggl-product-detail media-gallery').forEach(gallery => {
      if (gallery.dataset.gglEnhanced) return;
      const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      const track = viewer?.querySelector('[id^="Slider-Gallery-"]');
      if (!track || track.children.length < 2) return;
      gallery.dataset.gglEnhanced = 'true';
      const controller = new AbortController();
      const go = index => {
        const slides = [...track.children];
        gallery.setActiveMedia(slides[Math.max(0, Math.min(index, slides.length - 1))].dataset.mediaId, false);
      };
      bindDrag(track, controller.signal, go);
      viewer.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = Math.round(track.scrollLeft / track.clientWidth);
        go(event.key === 'Home' ? 0 : event.key === 'End' ? track.children.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
      });
      viewer.addEventListener('slideChanged', event => {
        const active = event.detail.currentElement;
        if (!active) return;
        const changed = !active.classList.contains('is-active');
        [...track.children].forEach(slide => slide.classList.toggle('is-active', slide === active));
        if (changed) gallery.playActiveMedia(active);
        const status = gallery.querySelector('[id^="GalleryStatus-"]');
        if (status) { status.removeAttribute('aria-hidden'); status.textContent = 'სურათი ' + event.detail.currentPage + ' / ' + track.children.length; }
      });
      let galleryWidth = track.clientWidth;
      const resize = new ResizeObserver(() => {
        const width = track.clientWidth;
        if (!width || width === galleryWidth) return;
        galleryWidth = width;
        const active = track.querySelector('.is-active') || track.firstElementChild;
        track.scrollTo({left: active.offsetLeft, behavior: 'instant'});
        viewer.resetPages?.();
      });
      resize.observe(track);
      viewer.resetPages?.();
    });
  }
  customElements.whenDefined('media-gallery').then(() => enhanceGallery());
  document.addEventListener('shopify:section:load', event => enhanceGallery(event.target));
})();
